import React, { FunctionComponent, useState } from 'react';
import {
  Badge,
  ButtonBase,
  Chip,
  ChipProps,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import moment from 'moment';

import {
  IInvoice,
  INVOICE_STATUSES,
} from '../../../../../../store/V2/InvoicesSlice';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../../components/V2/Table/type1/TableStyles';
import { IUser } from '../../../../../../store/UserSlice';
import { checkUserPermission, getShortName } from '../../../../../../helper';
import { useAppSelector } from '../../../../../../hooks';
import { getAuthState } from '../../../../../../store/AuthSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../store/V2/RolesSlice';

interface InvoiceTableRowProps {
  invoice: IInvoice;
  onView: () => void;
  onUpdate: () => void;
  onResendMail: () => void;
  viewAdminNotes: () => void;
}

const InvoiceTableRow: FunctionComponent<InvoiceTableRowProps> = ({
  invoice,
  onView,
  onUpdate,
  onResendMail,
  viewAdminNotes,
}) => {
  // Store
  const authState = useAppSelector(getAuthState);

  // State
  const [tooltipOpen, setToolTipOpen] = useState(false);

  const authUser = authState.user;
  const updatePermission = checkUserPermission(authUser, {
    adminSettings: [USER_PERMISSION_INDIVIDUALS.ADMIN_SETTINGS_INVOICES_EDIT],
  });

  const updatedByUserData = invoice.updatedBy as IUser;
  const invoiceNumber = `INV-${invoice.invoiceNumber
    .toString()
    .padStart(4, '0')}`;

  const createdAtMoment = moment(invoice.createdAt);
  const statusChipProps: ChipProps = {
    size: 'small',
    label: invoice.status.toLocaleLowerCase(),
    color: 'warning',
    sx: {
      textTransform: 'capitalize',
      width: '100%',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: 600,
    },
  };

  const updatedByUserName = updatedByUserData
    ? getShortName(
        updatedByUserData.firstname,
        updatedByUserData.lastname,
        true
      )
    : '-';

  const userData = invoice.user as IUser;

  switch (invoice.status) {
    case INVOICE_STATUSES.PENDING:
      statusChipProps.color = 'warning';
      break;

    case INVOICE_STATUSES.PAID:
      statusChipProps.color = 'success';
      break;

    case INVOICE_STATUSES.VOID:
      statusChipProps.color = 'default';
      break;

    default:
      break;
  }

  const handleCopyReference = () => {
    if (invoice.stripeSessionId) {
      setToolTipOpen(true);

      window.navigator.clipboard.writeText(invoice.stripeSessionId);

      setTimeout(() => {
        setToolTipOpen(false);
      }, 1000);
    }
  };

  return (
    <StyledTableRow hover>
      <StyledTableCell>
        <Stack>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {createdAtMoment.format('ddd MMM DD YYYY')}
          </Typography>
          <Typography fontSize="12px" color="#94A3B8">
            {createdAtMoment.format('hh:mm A')}
          </Typography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {invoiceNumber}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {getShortName(userData.firstname, userData.lastname, true)}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
          textTransform="capitalize"
        >
          {invoice.paymentMethod.toString().toLowerCase()}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Chip {...statusChipProps} />
      </StyledTableCell>
      <StyledTableCell>
        {invoice.stripeSessionId ? (
          <Tooltip
            disableFocusListener
            disableHoverListener
            disableTouchListener
            open={tooltipOpen}
            title="Coped to clipboard"
            PopperProps={{
              disablePortal: true,
            }}
          >
            <ButtonBase onClick={handleCopyReference}>
              <Stack direction="row" alignItems="center">
                <ContentCopyIcon sx={{ fontSize: '12px' }} />
                &nbsp;
                <Typography
                  fontSize="14px"
                  fontWeight={500}
                  color="#1E293B"
                  textTransform="capitalize"
                >
                  {invoice.stripeSessionId.slice(0, 15)}...
                </Typography>
              </Stack>
            </ButtonBase>
          </Tooltip>
        ) : (
          <Typography
            fontSize="14px"
            fontWeight={500}
            color="#1E293B"
            className="ellipsis-text two-lines"
          >
            -
          </Typography>
        )}
      </StyledTableCell>
      <StyledTableCell>
        <Badge badgeContent={(invoice.adminNotes || []).length} color="primary">
          <ButtonBase onClick={viewAdminNotes}>
            <Typography
              fontSize="14px"
              fontWeight={500}
              color="#1E293B"
              textTransform="capitalize"
              sx={{ textDecoration: 'underline' }}
            >
              View Admin Notes
            </Typography>
          </ButtonBase>
        </Badge>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {updatedByUserName}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Stack direction="row" alignItems="center">
          <Tooltip title="View">
            <IconButton onClick={onView}>
              <VisibilityIcon sx={{ color: '#1E293B' }} />
            </IconButton>
          </Tooltip>
          {updatePermission ? (
            <Tooltip title="Edit Status">
              <IconButton
                onClick={onUpdate}
                disabled={invoice.status === INVOICE_STATUSES.PAID}
              >
                <EditIcon
                  sx={
                    invoice.status !== INVOICE_STATUSES.PAID
                      ? { color: '#1E293B' }
                      : undefined
                  }
                />
              </IconButton>
            </Tooltip>
          ) : null}
          {updatePermission ? (
            <Tooltip title="Resend Mail">
              <IconButton
                onClick={onResendMail}
                disabled={invoice.status !== INVOICE_STATUSES.PENDING}
              >
                <EmailIcon
                  sx={
                    invoice.status !== INVOICE_STATUSES.PENDING
                      ? undefined
                      : { color: '#1E293B' }
                  }
                />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </StyledTableCell>
    </StyledTableRow>
  );
};

export default InvoiceTableRow;
